import React, { useMemo, useRef } from 'react';
import { getEnvironmentVariables } from 'services/environment-variables/environment-variables';
import { type Venue, OperatingHours } from 'generated/graphql';
import InputTitleWrap from 'components/Inputs/InputTitleWrap';
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';

import AlertIcon from 'icons/alert_info.svg';
import { formatToReadableDate } from 'utils/dateTimeHelpers';
import { type ClosedDates } from 'types';
import { DEFAULT_TIMEZONE } from 'consts';
import styles from './ClosedHours.module.scss';

const { CLOSED_DATES_WHITELISTED_APP_IDS } = getEnvironmentVariables();

/**
 * Function to change date by days (negative/positive) in YYYY-MM-DD format
 * Example: changeDateByDays('2021-08-01', 1) => '2021-08-02'
 */
export const changeDateByDays = (dateStr: string, days: number): string => {
  return DateTime.fromISO(dateStr).plus({ days }).toISODate() || '';
};

/**
 * Function to parse ClosedDates object and return error string message if any
 */
export const parseClosedDatesForErrors = (
  closedDates: ClosedDates,
  today: string,
  maxDate: string
): string | undefined => {
  const { from, to, isRange } = closedDates;

  if (isRange) {
    if (from && to && to <= from) {
      return `"Closed To" date should be greater than "Closed From" date`;
    }
    if (!from) {
      return `"Closed From" date is invalid`;
    }
    if (!to) {
      return `"Closed To" date is invalid`;
    }
    if (from < today) {
      return `"Closed From" date should be today or in future`;
    }
    if (from > maxDate) {
      return `"Closed From" date should be within next 10 years`;
    }
    if (to > maxDate) {
      return `"Closed To" date should be within next 10 years`;
    }
  } else {
    if (!from) {
      return `"Closed On" date is invalid`;
    }
    if (from < today) {
      return `"Closed On" date should be today or in future`;
    }
    if (from > maxDate) {
      return `"Closed On" date should be within next 10 years`;
    }
  }
  return undefined;
};

type Props = {
  currentVenue?: Venue;
  venueMMDates: ClosedDates[];
  currentActiveClosures: OperatingHours[];
  onChangeVenueMMDates: (dates: ClosedDates[]) => void;
  onChangeSavedActiveClosures: (specialHours: OperatingHours[]) => void;
};

const ClosedHours: React.FC<Props> = ({
  currentVenue,
  venueMMDates,
  currentActiveClosures,
  onChangeVenueMMDates,
  onChangeSavedActiveClosures
}) => {
  const fromDateInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const timezone = currentVenue?.timezone || DEFAULT_TIMEZONE;

  const today = DateTime.now().setZone(timezone).toISODate() || '';

  // Set max date to 10 years from today
  const maxDate = DateTime.fromISO(today).plus({ years: 10 }).toISODate() || '';

  const handleTimeChange = (
    index: number,
    type: Exclude<keyof ClosedDates, 'isRange'>,
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const newValue = event.target.value;
    const clonedClosedDates = cloneDeep(venueMMDates);

    clonedClosedDates[index][type] = newValue;
    clonedClosedDates[index].error = undefined;

    // If "Closed From" date is greater than "Closed To" date, update "Closed To" date to be 1 day after "Closed From" date.
    if (clonedClosedDates[index].isRange && type === 'from' && clonedClosedDates[index].to <= newValue) {
      clonedClosedDates[index].to = changeDateByDays(newValue, 1);
    }
    clonedClosedDates[index].error = parseClosedDatesForErrors(clonedClosedDates[index], today, maxDate);
    onChangeVenueMMDates(clonedClosedDates);
  };

  const addClosedDates = (): void => {
    onChangeVenueMMDates([
      { from: changeDateByDays(today, 1), to: changeDateByDays(today, 2), isRange: false },
      ...venueMMDates
    ]);
    // Focus the newly added input field
    setTimeout(() => {
      fromDateInputRefs.current[0]?.focus();
    }, 50);
  };

  const removeSavedDates = (index: number): void => {
    const updatedSavedClosedDates = currentActiveClosures.filter((_, i) => i !== index);
    onChangeSavedActiveClosures(updatedSavedClosedDates);
  };

  const removeClosedDates = (index: number): void => {
    const updatedClosedDates = venueMMDates.filter((_, i) => i !== index);
    onChangeVenueMMDates(updatedClosedDates);
  };

  const { unsupportedApps, supportedApps } = useMemo(() => {
    if (!currentVenue?.menus) return { unsupportedApps: [], supportedApps: [] };

    return currentVenue.menus.reduce<{ unsupportedApps: string[]; supportedApps: string[] }>(
      (acc, { apps }) => {
        const appId = Number(apps[0].id?.startsWith('virtual') ? apps[0].id.split('.')[1] : apps[0].id);

        if (!CLOSED_DATES_WHITELISTED_APP_IDS.includes(appId) && !acc.unsupportedApps.includes(apps[0].name)) {
          acc.unsupportedApps.push(apps[0].name);
        } else if (CLOSED_DATES_WHITELISTED_APP_IDS.includes(appId) && !acc.supportedApps.includes(apps[0].name)) {
          acc.supportedApps.push(apps[0].name);
        }

        return acc;
      },
      { unsupportedApps: [], supportedApps: [] }
    );
  }, [currentVenue]);

  return (
    <div className={styles.ClosedHours} data-testid="operating-hours-specialHours">
      <div className={styles.Header}>
        <p>
          Expecting to close on an upcoming holiday? Easily close all your app menus and stop your customers from
          ordering online.
        </p>
      </div>

      {supportedApps.length > 0 ? (
        <div className={styles.Content}>
          <button
            onClick={addClosedDates}
            className={styles.AddDate}
            type="button"
            tabIndex={0}
            aria-label="Add Closed Date Range"
            data-testid="add-closed-date"
          >
            <i className="mm-plus" />
            <span>Add Closed date</span>
          </button>
          {(venueMMDates.length > 0 || currentActiveClosures.length > 0) && (
            <div className={styles.ClosedDatesList}>
              {venueMMDates.map(({ from, to, isRange }, index) =>
                isRange ? (
                  /* cannot use any other available keys as it will cause issues when editing time ranges */
                  /* eslint-disable react/no-array-index-key */
                  <div key={index} className={styles.Row}>
                    <div className={styles.ClosedDates}>
                      <div className={styles.ClosedDatesText}>From</div>
                      <div className={styles.ClosedDatesInput}>
                        <InputTitleWrap title="Closed From">
                          <input
                            type="date"
                            required
                            value={from}
                            min={today}
                            max={maxDate}
                            onChange={(event) => handleTimeChange(index, 'from', event)}
                            ref={(el) => (fromDateInputRefs.current[index] = el)}
                            data-testid={`closed-from-${index}`}
                          />
                        </InputTitleWrap>
                      </div>
                      <div className={`${styles.ClosedDatesText} ${styles.CenterAligned}`}>to</div>
                      <div className={styles.ClosedDatesInput}>
                        <InputTitleWrap title="Closed To">
                          <input
                            type="date"
                            required
                            value={to}
                            min={changeDateByDays(from, 1)}
                            max={maxDate}
                            onChange={(event) => handleTimeChange(index, 'to', event)}
                            data-testid={`closed-to-${index}`}
                          />
                        </InputTitleWrap>
                      </div>
                      <div className={styles.ClosedDatesAction}>
                        <button
                          onClick={() => {
                            const updatedRanges = [...venueMMDates];
                            updatedRanges[index] = {
                              from: venueMMDates[index].from,
                              to: venueMMDates[index].to,
                              isRange: false
                            };
                            onChangeVenueMMDates(updatedRanges);
                            // Focus the "On" date input field for this index
                            fromDateInputRefs.current[index]?.focus();
                          }}
                          className={styles.HideRange}
                          type="button"
                          tabIndex={0}
                          aria-label="Add Ranges"
                        >
                          <span>Single Date</span>
                        </button>
                        <button
                          className={styles.RemoveClosedDates}
                          aria-label="Remove date range"
                          data-testid={`remove-date-range-${index}`}
                          onClick={() => removeClosedDates(index)}
                          type="button"
                        >
                          <i className="mm-trash-o" />
                        </button>
                      </div>
                    </div>
                    {venueMMDates[index].error && <div className={styles.Error}>{venueMMDates[index].error}</div>}
                  </div>
                ) : (
                  /* cannot use any other available keys as it will cause issues when editing time ranges */
                  /* eslint-disable react/no-array-index-key */
                  <div key={index} className={styles.Row}>
                    <div className={styles.SingleDate}>
                      <div className={styles.SingleDateLabel}>Close menus for a single date</div>
                      <div className={styles.SingleDateInput}>
                        <InputTitleWrap title="Closed On">
                          <input
                            type="date"
                            required
                            value={from}
                            min={today}
                            max={maxDate}
                            onChange={(event) => handleTimeChange(index, 'from', event)}
                            ref={(el) => (fromDateInputRefs.current[index] = el)}
                            data-testid={`closed-on-${index}`}
                          />
                        </InputTitleWrap>
                      </div>
                      <div className={styles.SingleDateAction}>
                        <button
                          onClick={() => {
                            const updatedRanges = [...venueMMDates];
                            updatedRanges[index] = {
                              from: venueMMDates[index].from,
                              to: changeDateByDays(venueMMDates[index].from, 1),
                              isRange: true
                            };
                            onChangeVenueMMDates(updatedRanges);
                            // Focus the "From" date input field for this index
                            fromDateInputRefs.current[index]?.focus();
                          }}
                          className={styles.ShowRanges}
                          type="button"
                          tabIndex={0}
                          aria-label="Show Date Ranges"
                        >
                          <span>Date Range</span>
                        </button>
                        <button
                          className={styles.RemoveDate}
                          aria-label="Remove date range"
                          onClick={() => removeClosedDates(index)}
                          type="button"
                        >
                          <i className="mm-trash-o" />
                        </button>
                      </div>
                    </div>
                    {venueMMDates[index].error && <div className={styles.Error}>{venueMMDates[index].error}</div>}
                  </div>
                )
              )}
              <div className={styles.SavedDateList}>
                {currentActiveClosures?.map(({ dates }, index) => {
                  if (!Array.isArray(dates) || dates.length === 0) return null; // Fallback safety check
                  return (
                    /* cannot use any other available keys as it will cause issues when editing time ranges */
                    /* eslint-disable react/no-array-index-key */
                    <div key={index} className={styles.SavedDate}>
                      <div className={styles.SavedDateText}>
                        Closed{' '}
                        {dates[0] && dates[1] && (
                          <>
                            from <b>{formatToReadableDate(dates[0])}</b> to <b>{formatToReadableDate(dates[1])}</b>
                          </>
                        )}
                        {dates[0] && !dates[1] && (
                          <>
                            for a single date on <b>{formatToReadableDate(dates[0])}</b>
                          </>
                        )}
                        .
                      </div>
                      <button
                        className={styles.RemoveSavedDate}
                        aria-label="Remove date range"
                        onClick={() => removeSavedDates(index)}
                        type="button"
                      >
                        <i className="mm-trash-o" />
                      </button>
                    </div>
                  );
                })}
              </div>
              {unsupportedApps.length > 0 && (
                <div className={styles.UnsupportedApp}>
                  <img src={AlertIcon} alt="Warning" />
                  <div>
                    All app menus except{' '}
                    {unsupportedApps.map((app, index) => (
                      <React.Fragment key={app}>
                        {index > 0 && ', '}
                        <b>{app}</b>
                      </React.Fragment>
                    ))}{' '}
                    will be closed. To close your storefront in {unsupportedApps.join(', ')} refer to your app or app
                    support team.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className={styles.ClosedHoursNotSupported}>
          <p>No supported apps subscribed.</p>
        </div>
      )}
    </div>
  );
};

export default ClosedHours;
